import { Comment as CommentType } from "@/src/types/comments";
import { useContext, useEffect, useState } from "react";
import ArticleCommentsHeader from "./Header";
import ArticleCommentsTextInput from "./TextInput";
import { addComment } from "../api";
import Comment from "../Comment";
import ContentContainer from "../../containers/ContentContainer";
import { AuthContext } from "@/pages/_app";
import { Alert } from "@material-tailwind/react";

export interface ArticleCommentsProps {
  postId: number;
  comments: CommentType[];
}

export interface CommentWithChildren extends CommentType {
  children: CommentWithChildren[];
}

interface GroupedComment {
  comment: CommentType;
  children?: GroupedComment[];
}

export interface CommentMap {
  [id: string]: CommentWithChildren;
}

const groupComments = (comments: CommentType[]): GroupedComment[] => {
  const commentMap: Record<string, GroupedComment> = {};

  // Create a map of comments using their IDs
  comments.forEach((comment) => {
    //@ts-ignore
    commentMap[comment.databaseId] = { ...comment, children: [] };
  });

  // Populate the children array based on parent IDs
  comments.forEach((comment) => {
    if (comment.parentDatabaseId && commentMap[comment.parentDatabaseId]) {
      //@ts-ignore
      commentMap[comment.parentDatabaseId].children.push(
        commentMap[comment.databaseId]
      );
    }
  });

  // Find top-level comments (comments with no parent ID)
  const topLevelComments = comments.filter(
    (comment) => !comment.parentDatabaseId
  );

  // Return the grouped comments

  return topLevelComments.map((comment) => commentMap[comment.databaseId]);
};

const ArticleComments = ({ postId, comments }: ArticleCommentsProps) => {
  const [commentMap, setCommentMap] = useState<GroupedComment[]>([]);
  const [alertMessage, setAlertMessage] = useState({ type: "", message: "" });
  const [canComment, setCanComment] = useState(false);

  const { authContext } = useContext(AuthContext);
  useEffect(() => {
    if (authContext?.userEmail && authContext?.userEmail !== "") {
      setCanComment(true);
    }
  }, [authContext?.userEmail]);
  useEffect(() => {
    setCommentMap(groupComments(comments));
  }, [comments]);

  const onCommentSubmit = async (text: string) => {
    const resp = await addComment({
      commentOn: postId,
      content: text,
      token: authContext?.authToken ?? "",
    })
      .then(() => {
        setAlertMessage({
          type: "success",
          message: "Comment sent - awaiting approval",
        });

        setTimeout(() => {
          setAlertMessage({ type: "", message: "" });
        }, 2000);
        return true;
      })
      .catch((e) => {
        setAlertMessage({ type: "error", message: "Error posting comment" });

        setTimeout(() => {
          setAlertMessage({ type: "", message: "" });
        }, 2000);

        return false;
      });
    return resp;
  };

  const handleCommentReply = async (id: number, text: string) => {
    const resp = await addComment({
      commentOn: postId,
      content: text,
      parentId: id,
      token: authContext?.authToken ?? "",
    })
      .then(() => {
        setAlertMessage({
          type: "success",
          message: "Comment sent - awaiting approval",
        });
        setTimeout(() => {
          setAlertMessage({ type: "", message: "" });
        }, 2000);
        return true;
      })
      .catch((e) => {
        setAlertMessage({ type: "error", message: "Error posting comment" });
        setTimeout(() => {
          setAlertMessage({ type: "", message: "" });
        }, 2000);
        return false;
      });
  };

  const renderComments = (comments: GroupedComment[]) => {
    return (
      <ul className="ml-2">
        {comments.map((comment) => (
          <li key={comment.comment?.databaseId}>
            <Comment
              //@ts-ignore
              comment={comment as CommentType}
              onReply={handleCommentReply}
            />

            {
              //@ts-ignore
              comment?.children?.length > 0 &&
                renderComments(comment.children ?? [])
            }
          </li>
        ))}
      </ul>
    );
  };

  return (
    <ContentContainer>
      <ArticleCommentsHeader amount={comments.length} />

      <div className="article-comments-container">
        {renderComments(commentMap)}
      </div>
      <ArticleCommentsTextInput
        onSubmit={onCommentSubmit}
        disabled={!canComment}
      />
      {alertMessage.type !== "" && (
        <Alert
          color={alertMessage.type === "error" ? "red" : "blue"}
          className="fixed bottom-2 right-2 w-auto z-50"
        >
          {alertMessage.message}
        </Alert>
      )}
    </ContentContainer>
  );
};

export default ArticleComments;
